import { store } from '@/app/store'
import { BASE_API_URL, COOKIE_SESSION, MFA_CLIENT_CAPABILITIES } from '@/constants/Common'
import { ROUTE_FORGOT_PASSWORD } from '@/constants/Routes'
import { hasLimitedView, hasPasswordReset, hasSubscriptionRequired, REQUIRED_ACTION_URN, requirementsFrom } from '@/types'
import { clearAuthCookies } from '@/utils/authCookies'
import { resetAuth, setRefresh, setToken, setStrongMfaSetupRequired } from '@/pages/auth/authSlice'
import { resetClub } from '@/pages/club/clubSlice'
import { resetEvent } from '@/pages/event/eventSlice'
import { resetPlayer } from '@/pages/player/playerSlice'
import { resetUser } from '@/pages/user/userSlice'
import { queryClient } from '@/queryClient'
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import toast from 'react-hot-toast'
import { logout } from './user'
interface CustomRequestConfig extends AxiosRequestConfig {
  'x-refresh-token'?: string
  'x-password'?: string
}

axios.defaults.baseURL = BASE_API_URL
// Send the HttpOnly session cookie on every (cross-origin) call. The web app and the API share a
// registrable domain per environment (dashboard.dupr.com ↔ api.dupr.com; uat.dupr.gg ↔
// api.uat.dupr.gg), so the cookie is first-party and SameSite=Lax lets it through.
axios.defaults.withCredentials = true

const axiosInstance = axios.create({ withCredentials: true })

const axiosInstanceRefresh = axios.create({ withCredentials: true })

axiosInstanceRefresh.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  async (error: any) => {
    // A cut first-party session refreshes into a 428 PASSWORD_RESET_REQUIRED; route to reset
    // instead of the generic "Session Expired" so the user knows why and where to go.
    if (handlePasswordResetRequired(error)) {
      return Promise.reject(error)
    }
    if (error?.response?.status === 401) {
      logUserOut()
      toast.error('Session Expired', {
        id: 'Expired',
      })
    }
    return Promise.reject(error)
  }
)

export function logUserOut() {
  const { chatClient } = store.getState().user
  // Logout authenticates via the session cookie now (sent with credentials); no bearer to pass.
  logout()
  clearAuthCookies()
  chatClient?.disconnectUser()
  store.dispatch(resetAuth())
  store.dispatch(resetUser())
  store.dispatch(resetEvent())
  store.dispatch(resetPlayer())
  store.dispatch(resetClub())
  queryClient.clear()
}

// A club-staff (or admin-forced) account must reset its password before it gets a session. The
// server signals it two ways: a 428 PASSWORD_RESET_REQUIRED on the login/2fa path, and — for a
// session that was cut mid-flight — a 401 on the request whose refresh then answers 428. Either
// way we drop the local session and send the browser to the forgot-password flow. Returns true
// when it handled the error, so callers can stop (and not loop the refresh retry).
export function handlePasswordResetRequired(error: any): boolean {
  // Driven by the rich `requirements` (the password-reset-required URN), not the legacy errorCode.
  // Legacy fallback (if a body ever omits the rich array): error?.response?.data?.errorCode === 'PASSWORD_RESET_REQUIRED'.
  if (!hasPasswordReset(requirementsFrom(error))) return false
  logUserOut()
  toast.error('Please reset your password to continue', { id: 'PasswordResetRequired' })
  if (!window.location.pathname.includes(ROUTE_FORGOT_PASSWORD)) {
    window.location.assign(ROUTE_FORGOT_PASSWORD)
  }
  return true
}

// A post-session enforcement 428 whose `requirements` include the strong-mfa-setup action means the
// account has a session but is NOT yet fully authenticated — it must enrol a strong second factor
// before it can do anything else. Flip the redux flag; a top-level gate (App) then renders ONLY the
// enrolment flow and suppresses every other route, query, modal and background effect until it is
// satisfied. We deliberately do NOT log out or `window.location.assign` here: a full page reload
// would tear down the SPA state the gate rides on, and the session itself is still valid. Returns
// true when it handled the error, so callers can stop.
export function handleStrongMfaSetupRequired(error: any): boolean {
  const requirements = error?.response?.data?.requirements
  const needsSetup =
    Array.isArray(requirements) &&
    requirements.some((r: { type?: string }) => r?.type === REQUIRED_ACTION_URN.STRONG_MFA_SETUP)
  if (!needsSetup) return false
  store.dispatch(setStrongMfaSetupRequired(true))
  return true
}

export async function refreshToken(config: CustomRequestConfig) {
  // First-party refresh is cookie-based: the HttpOnly refresh cookie is sent automatically and the
  // server rotates the session into new cookies. No token crosses JS, so we just retry the original
  // request once the cookie is refreshed.
  await axiosInstanceRefresh
    // First-party refresh is the cookie-only POST (segregated from the partner GET+x-refresh-token).
    // The HttpOnly refresh cookie rides withCredentials; the server rotates the session into cookies.
    .post('/auth/v2.0/refresh')
    .then(() => {
      store.dispatch(setToken(COOKIE_SESSION))
      store.dispatch(setRefresh(COOKIE_SESSION))
    })
    .catch((err: AxiosError) => {
      if (err.response?.status === 400) {
        toast.error('Failed to refresh token', {
          id: 'RefreshFailed',
        })
        logUserOut()
      }
      if (err.response?.status === 401) {
        toast.error('Session Expired', {
          id: 'Expired',
        })
        logUserOut()
      }
    })

  return axios(config as any)
}

axiosInstance.interceptors.response.use(
  async (response: AxiosResponse) => {
    return response
  },
  async (error: any) => {
    const config = error?.config
    if (error?.response?.status === 401) {
      // Only attempt a refresh for a session that actually has one. Without this guard an
      // anonymous visitor's 401 fires a refresh with an empty x-refresh-token, and that call's
      // own failure toasts "Session Expired" / "Failed to refresh token" and runs logUserOut()
      // — telling someone who was never logged in that their session ended. That hit every
      // anonymous page, the password-reset flow included.
      const { refresh } = store.getState().auth
      if (refresh) {
        return refreshToken(config)
      }
    }
    return Promise.reject(error)
  }
)

axiosInstance.interceptors.response.use(
  async (response: AxiosResponse) => {
    return response
  },
  async (error: any) => {
    if (error?.response?.status === 428) {
      // A must-reset account (club staff, or admin-forced) is blocked at login/2fa until it resets.
      if (handlePasswordResetRequired(error)) {
        return Promise.reject(error)
      }
      // A session that must enrol a strong second factor before continuing.
      if (handleStrongMfaSetupRequired(error)) {
        return Promise.reject(error)
      }
      // Branch on the rich `requirements` URNs, not the legacy errorCode. Legacy fallback (if a body
      // ever omits the rich array): errorCode === 'SUBSCRIPTION_REQUIRED' / 'LIMITED_VIEW'.
      const requirements = requirementsFrom(error)
      // Redirect to external redeem page
      if (window.location.pathname !== '/membership-required' && hasSubscriptionRequired(requirements)) {
        // setTimeout(() => {
        window.location.assign('/membership-required')
        // }, 10000)
      } else if (hasLimitedView(requirements)) {
        // Feature-quota enforcement (e.g. profile views) blocked the request.
        toast.error('Limits Hit', { id: 'LimitedView' })
      }
      // Optionally, return a rejected promise to halt further processing
      return Promise.reject(error)
    }
    return Promise.reject(error)
  }
)

const createAxiosConfig = (config: CustomRequestConfig = {}) => {
  const AxiosConfig = {
    ...config,
    headers: {
      // No Authorization bearer: the first-party session authenticates via the HttpOnly cookie
      // (sent with credentials). x-refresh-token / x-password remain for the few flows that pass
      // them explicitly.
      ...(config['x-refresh-token'] && {
        'x-refresh-token': config['x-refresh-token'],
      }),
      ...(config['x-password'] && {
        'x-password': config['x-password'],
      }),
      Accept: 'application/json',
      'Content-Type': 'application/json; charset=utf-8',
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      Expires: '0',
      // Advertise this client's strong-factor capabilities on every call — the login POST goes
      // through here, so this is what makes login offer TOTP/passkey (the server offers the
      // intersection of enrolled ∩ can-perform). Advisory only.
      'X-DUPR-Client-Capabilities': MFA_CLIENT_CAPABILITIES,
      ...config.headers,
    },
  }
  return AxiosConfig
}

/**
 * The message a failed request should show a user.
 *
 * axios rejects on any non-2xx, so the server's explanation never reaches the response body a caller
 * unwrapped — it is on the rejected error. Reading it here is what lets a UI show the sentence the
 * server actually wrote ("This benefit is not claimable", "This club's benefits are managed by
 * DUPR") rather than its own generic wording or, worse, axios's "Request failed with status code
 * 400". Falls back when the rejection is not a DUPR error body at all: a network failure, a timeout,
 * a thrown string.
 */
export const serverErrorMessage = (error: unknown, fallbackMessage: string): string => {
  const data = (error as { response: { data: { message: string | null } | null } | null })?.response?.data
  return data?.message || fallbackMessage
}

export default class httpService {
  static async Get<T>(url: string, config: CustomRequestConfig = {}) {
    return await axiosInstance.get<T>(url, createAxiosConfig(config)).then((res) => res.data)
  }

  /**
   * A GET whose full response is returned, headers included.
   *
   * Every other verb here unwraps to `res.data`, which is right almost always — a caller wants the
   * body. This exists for the one case that cannot work that way: an endpoint that answers JSON or
   * a file at the same URL depending on whether a background job has finished, where the content
   * type is the only thing that says which arrived.
   */
  static async GetRaw<T>(url: string, config: CustomRequestConfig = {}) {
    return await axiosInstance.get<T>(url, createAxiosConfig(config))
  }

  static async Post<T>(url: string, data: any, config: CustomRequestConfig = {}) {
    return await axiosInstance.post<T>(url, data, createAxiosConfig(config)).then((res) => res.data)
  }

  // Typed like its four siblings, but with a default: it was the only verb without a response type,
  // so callers that wanted one had to cast — and a cast is not a check. The `= any` keeps the ten
  // existing callers that relied on the untyped return compiling exactly as they did.
  static async Put<T = any>(url: string, data: any, config: CustomRequestConfig = {}) {
    return await axiosInstance.put<T>(url, data, createAxiosConfig(config)).then((res) => res.data)
  }

  static async Patch<T>(url: string, data: any, config: CustomRequestConfig = {}) {
    return await axiosInstance.patch<T>(url, data, createAxiosConfig(config)).then((res) => res.data)
  }

  static async Delete<T>(url: string, config: CustomRequestConfig = {}) {
    return await axiosInstance.delete<T>(url, createAxiosConfig(config)).then((res) => res.data)
  }
}
